import React, { useState } from "react";
import { attachImagesToExplanation } from "../../../../Services/SystemExplanationsApi";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";

const AddExplanationImages = ({ explanationId, onSuccess }) => {
  const [images, setImages] = useState([{ title: "", description: "", image: null, preview: null }]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (index, e) => {
    const { name, value, files } = e.target;
    const newImages = [...images];
    if (name === "image") {
      const file = files[0];
      newImages[index][name] = file;
      newImages[index].preview = URL.createObjectURL(file); // Generate preview URL
    } else {
      newImages[index][name] = value;
    }
    setImages(newImages);
  };

  const handleAddRow = () => {
    setImages([...images, { title: "", description: "", image: null, preview: null }]);
  };

  const handleRemoveRow = (index) => {
    if (images.length > 1) {
      const newImages = images.filter((_, i) => i !== index);
      setImages(newImages);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      images.forEach((img, index) => {
        formData.append(`images[${index}][title]`, img.title);
        formData.append(`images[${index}][description]`, img.description);
        formData.append(`images[${index}][image]`, img.image);
      });

      await attachImagesToExplanation(explanationId, formData);
      alert("✅ تم رفع الصور بنجاح!");
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("❌ Error attaching images:", error);
      alert("فشل رفع الصور.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: '#4caf50', width: 60, height: 60, margin: '0 auto', mb: 1 }}>
          <PhotoLibraryIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
          إضافة صور للشرح
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          أضف صور توضيحية للشرح مع العناوين والأوصاف
        </Typography>
        <Chip 
          label={`${images.length} صورة`}
          color="success"
          size="small"
          sx={{ mt: 1 }}
        />
      </Box>

      <Grid container spacing={3}>
        {images.map((img, index) => (
          <Grid item xs={12} key={index}>
            <Card 
              elevation={3}
              sx={{
                borderRadius: 2,
                border: '2px solid #4caf50',
                position: 'relative',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: 6,
                }
              }}
            >
              {/* رقم الصورة */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -15,
                  left: 20,
                  bgcolor: '#4caf50',
                  color: 'white',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  boxShadow: 3,
                  zIndex: 1
                }}
              >
                {index + 1}
              </Box>

              {/* زر الحذف */}
              {images.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -15,
                    right: 20,
                    zIndex: 1
                  }}
                >
                  <Tooltip title="حذف الصورة">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveRow(index)}
                      sx={{
                        bgcolor: '#ffebee',
                        '&:hover': {
                          bgcolor: '#ffcdd2',
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <CardContent sx={{ pt: 4 }}>
                <Grid container spacing={2}>
                  {/* عنوان الصورة */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <TitleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            عنوان الصورة
                          </Typography>
                        </Box>
                        <TextField
                          name="title"
                          value={img.title}
                          onChange={(e) => handleChange(index, e)}
                          fullWidth
                          required
                          placeholder="أدخل عنوان الصورة..."
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: 'white' }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* وصف الصورة */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <DescriptionIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            وصف الصورة
                          </Typography>
                        </Box>
                        <TextField
                          name="description"
                          value={img.description}
                          onChange={(e) => handleChange(index, e)}
                          fullWidth
                          required
                          placeholder="أدخل وصف الصورة..."
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: 'white' }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* تحميل الصورة */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: '#f1f8e9' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <ImageIcon sx={{ color: '#689f38', fontSize: 22 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#689f38' }}>
                            الصورة
                          </Typography>
                          <Chip label="مطلوب" size="small" color="success" />
                        </Box>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            py: 1.5,
                            borderStyle: 'dashed',
                            borderWidth: 2,
                            borderColor: '#4caf50',
                            color: '#4caf50',
                            '&:hover': {
                              borderWidth: 2,
                              borderColor: '#388e3c',
                              bgcolor: '#e8f5e9'
                            }
                          }}
                        >
                          {img.image ? 'تغيير الصورة' : 'اختر صورة للتحميل'}
                          <input
                            type="file"
                            name="image"
                            hidden
                            onChange={(e) => handleChange(index, e)}
                            accept="image/*"
                          />
                        </Button>
                        {img.image && (
                          <Box sx={{ mt: 1, textAlign: 'center' }}>
                            <Chip 
                              label={`✓ تم اختيار: ${img.image.name}`}
                              color="success"
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* معاينة الصورة */}
                  {img.preview && (
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ bgcolor: 'white' }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#4caf50' }}>
                            معاينة الصورة:
                          </Typography>
                          <Box
                            sx={{
                              position: 'relative',
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '3px solid #4caf50',
                              boxShadow: 2
                            }}
                          >
                            <CardMedia
                              component="img"
                              height="200"
                              image={img.preview}
                              alt="Preview"
                              sx={{ objectFit: 'contain', bgcolor: '#fafafa' }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* أزرار الإضافة والإرسال */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: '#4caf50',
              color: '#4caf50',
              fontWeight: 'bold',
              '&:hover': {
                borderWidth: 2,
                borderColor: '#388e3c',
                bgcolor: '#e8f5e9'
              }
            }}
          >
            إضافة صورة أخرى
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '1.1rem',
              background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #388e3c 30%, #4caf50 90%)',
                transform: 'scale(1.02)',
                transition: 'all 0.2s'
              },
              '&:disabled': {
                background: '#e0e0e0'
              }
            }}
          >
            {submitting ? 'جاري الرفع...' : 'رفع الصور'}
          </Button>
        </Grid>
      </Grid>

      {/* الصور المرفوعة */}
      {uploadedImages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Avatar sx={{ bgcolor: '#2196f3' }}>
              <PhotoLibraryIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              الصور المرفوعة
            </Typography>
            <Chip 
              label={uploadedImages.length}
              color="primary"
              size="small"
            />
          </Box>
          <Grid container spacing={2}>
            {uploadedImages.map((img) => (
              <Grid item xs={12} sm={6} md={4} key={img.id}>
                <Card 
                  elevation={3}
                  sx={{
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="160"
                    image={img.image_url}
                    alt={img.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {img.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {img.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default AddExplanationImages;
